import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { LayoutDashboard, Workflow, FileText, TrendingUp, ShoppingCart } from 'lucide-react';
import Autoplay from 'embla-carousel-autoplay';

const features = [
  {
    icon: LayoutDashboard,
    title: 'Unified Dashboard',
    description: 'See your entire portfolio health, recovery rates, and team performance at a glance.',
    bgColor: 'bg-brand-gold/10 border-brand-gold/30',
  },
  {
    icon: Workflow,
    title: 'Smart Workflows',
    description: 'Automate follow-ups, arrangements, and escalations with intelligent business rules.',
    bgColor: 'bg-brand-teal/10 border-brand-teal/30',
  },
  {
    icon: FileText,
    title: 'Automated Reports',
    description: 'Generate compliance reports, portfolio summaries, and investor updates instantly.',
    bgColor: 'bg-blue-500/10 border-blue-500/30',
  },
  {
    icon: TrendingUp,
    title: 'Calibration Analytics',
    description: 'Fine-tune your collection curves and valuation models with real performance data.',
    bgColor: 'bg-brand-gold/10 border-brand-gold/30',
  },
  {
    icon: ShoppingCart,
    title: 'Marketplace Listing',
    description: 'List portfolios with verified valuations and connect with pre-qualified buyers.',
    bgColor: 'bg-brand-teal/10 border-brand-teal/30',
  },
];

export function FeatureCarousel() {
  return (
    <section className="py-16 bg-card/50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold mb-4">Powerful Features</h2>
          <p className="text-xl text-muted-foreground">
            Everything you need in one secure workspace
          </p>
        </motion.div>

        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          plugins={[
            Autoplay({
              delay: 3000,
              stopOnInteraction: true,
            }),
          ]}
          className="w-full max-w-5xl mx-auto"
        >
          <CarouselContent>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className={`${feature.bgColor} hover:shadow-lg transition-all`}>
                      <CardContent className="p-6">
                        <div className="mb-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-gold/20 border border-brand-gold/40">
                            <Icon className="h-6 w-6 text-brand-gold" />
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                        <div className="mt-4 h-32 rounded bg-muted/50 flex items-center justify-center text-xs text-muted-foreground">
                          Screenshot
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </div>
    </section>
  );
}
